// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import classNames from "classnames";
import { formatDateTime } from "@transformers/date";
import { Icons, Pagination, TextField, IconButton } from "@components";
import * as notificationsSelectors from "@modules/reporting/selectors/notifications";
import sharedActionCreators from "@modules/shared/actionCreator";
import actionCreators from "@modules/reporting/actionCreator";
import type { Notification } from "@modules/reporting/reducer/notifications/types";
import withErrorHandler from "@components/error";
import config from "@config";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import type { PaginationExtendedType } from "@types";
import { isFirstCareUser } from "@modules/account/selectors";

import "./my-notifications.style.scss";

type PropsFromState = $Exact<{
  isFirstCareUser: boolean,
  notificationsSearch: string,
}>;

type PropsFromDispatch = $Exact<{
  getMyNotifications: typeof actionCreators.GetMyNotifications,
  getAbsenceNotification: typeof sharedActionCreators.GetEmployeeAbsenceNotification,
  setMyNotificationsSearch: typeof actionCreators.SetMyNotificationsSearch,
}>;
type Props = $Exact<{
  clientId: number,
  ...PropsFromPermissions,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
  myNotifications: Notification[],
  pagination: PaginationExtendedType,
}>;

type State = $Exact<{
  isTasksHidden: boolean,
  selectedPersonId: number | null,
}>;

export class DashboardNotificationsSection extends Component<Props, State> {
  state = {
    isTasksHidden: false,
    selectedPersonId: null,
  };

  componentDidMount() {
    this.props.getMyNotifications({
      clientId: this.props.clientId,
    });
  }

  handlePaginationChange = (page: number) => {
    this.props.getMyNotifications({
      clientId: this.props.clientId,
      page,
    });
  };

  selectPerson = (notification: Notification) => (event: SyntheticEvent<HTMLElement>) => {
    this.setState({
      selectedPersonId: Number(event.currentTarget.dataset.personId),
    });

    if (notification.employeeId && notification.absenceId) {
      this.props.getAbsenceNotification({
        clientId: this.props.clientId,
        employeeId: notification.employeeId,
        absenceId: notification.absenceId,
        notificationId: notification.id,
      });
    }
  };

  filterNotifications = () => {
    this.props.getMyNotifications({
      clientId: this.props.clientId,
      search: {
        employee: this.props.notificationsSearch,
      },
    });
  };

  onSearchNotificationsChange = (value: string) => {
    this.props.setMyNotificationsSearch({ notificationsSearch: value });
  };

  get notificationContent() {
    const currentNotification = this.props.myNotifications.find(
      el => el.id === this.state.selectedPersonId,
    );

    return currentNotification && currentNotification.content;
  }

  render() {
    return (
      <div className="d-flex flex-column my-notifications-wrapper">
        <div className="content-wrapper d-flex flex-row justify-content-center">
          <div className="persons-wrapper d-flex flex-column">
            <div className="persons-wrapper__search d-flex align-items-center justify-content-center">
              <div>
                <TextField
                  onChange={e => this.onSearchNotificationsChange(e.target.value)}
                  value={this.props.notificationsSearch}
                  placeholder={this.props.t("SEARCH_DOTS")}
                  className="persons-wrapper__search-input"
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      this.filterNotifications();
                    }
                  }}
                />
              </div>
              <IconButton
                icon={<span className="material-icons pointer">search</span>}
                onClick={this.filterNotifications}
              />
            </div>
            {this.props.myNotifications.map(el => (
              <div
                key={el.id}
                className={classNames(
                  "person-container d-flex flex-row justify-content-between align-items-center",
                  {
                    selected: el.id === this.state.selectedPersonId,
                  },
                )}
                onClick={this.selectPerson(el)}
                data-person-id={el.id}
              >
                <div className="person-wrapper d-flex flex-column">
                  <div className="person-id">{el.id}</div>
                  <div className="d-flex justify-content-start align-items-center">
                    <div className="person-name font-weight-bold mr-2">{el.employeeName}</div>
                    <div className="person-status mr-2">{formatDateTime(el.createdAt)}</div>
                    {el.isNew ? (
                      <span className="font-weight-bold text-warning">
                        {this.props.t("NEW_WARNING")}
                      </span>
                    ) : null}
                  </div>
                  <div className="person-status">{el.subject}</div>
                </div>
                <div className="person-arrow-icon">
                  <Icons.PersonArrowIcon />
                </div>
              </div>
            ))}
            <Pagination
              className="my-3 d-flex flex-fill justify-content-center"
              currentPage={this.props.pagination.pageNumber}
              totalPages={this.props.pagination.totalPages}
              onChange={this.handlePaginationChange}
            />
          </div>
          <div className="right-section d-flex align-items-center">
            {this.state.selectedPersonId && this.notificationContent && (
              <iframe
                title="notification-content"
                className="w-100 h-100 border-0"
                srcDoc={this.notificationContent}
              />
            )}
            {this.state.selectedPersonId && !this.notificationContent && (
              <div className="select-hint">{this.props.t("NOTIFICATION_DATA_EMPTY")}</div>
            )}
            {!this.state.selectedPersonId && (
              <div className="select-hint">{this.props.t("SELECT_NOTIFICATION_AT_LEFT")}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  myNotifications: notificationsSelectors.getMyNotifications(state),
  pagination: notificationsSelectors.getMyNotificationsPagination(state),
  isFirstCareUser: isFirstCareUser(state),
  notificationsSearch: notificationsSelectors.selectMyNotificationsSearch(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getMyNotifications: actionCreators.GetMyNotifications,
  getAbsenceNotification: sharedActionCreators.GetEmployeeAbsenceNotification,
  setMyNotificationsSearch: actionCreators.SetMyNotificationsSearch,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withErrorHandler(),
  withPermissions({
    permissionId: PermissionEnum.ACCESS_REPORTING_TOOLS,
  }),
)(DashboardNotificationsSection);
